import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import orgApi from '../../api/organizations';
import Drawer from '../UI/Drawer';
import {
  primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, fieldLabel, fieldLabelStyle, errMessage,
} from '../UI/ink';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="h-10 px-3.5 shrink-0 rounded-lg border text-[13px] font-medium"
      style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
    >
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
};

const InviteDrawer = ({ open, onClose, tenantId, roles, onInvited }) => {
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEmails([]);
    setDraft('');
    setError(null);
    setResults(null);
    const preferred = roles.find(r => !r.is_system) || roles[0];
    setRole(preferred?.name || '');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const addEmails = (raw) => {
    const parts = raw.split(/[\s,;]+/).map(p => p.trim().toLowerCase()).filter(Boolean);
    if (parts.length === 0) return true;
    const invalid = parts.filter(p => !EMAIL_RE.test(p));
    const valid = parts.filter(p => EMAIL_RE.test(p));
    setEmails(prev => [...prev, ...valid.filter(v => !prev.includes(v))]);
    if (invalid.length) {
      setError(`Not a valid email: ${invalid.join(', ')}`);
      setDraft(invalid.join(', '));
      return false;
    }
    setError(null);
    setDraft('');
    return true;
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmails(draft);
    } else if (e.key === 'Backspace' && !draft && emails.length) {
      setEmails(prev => prev.slice(0, -1));
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (/[\s,;]/.test(text.trim())) {
      e.preventDefault();
      addEmails(`${draft} ${text}`);
    }
  };

  const pendingDraft = EMAIL_RE.test(draft.trim()) ? [draft.trim().toLowerCase()] : [];
  const toSend = [...emails, ...pendingDraft.filter(d => !emails.includes(d))];

  const send = async () => {
    if (draft.trim() && !addEmails(draft)) return;
    if (toSend.length === 0) { setError('Add at least one email address.'); return; }
    if (!role) { setError('Pick a role.'); return; }
    setSending(true);
    setError(null);
    const out = [];
    for (const email of toSend) {
      try {
        const res = await orgApi.createInvitation(tenantId, email, role);
        out.push({ email, ok: true, emailSent: !!res.email_sent, link: res.invite_link || null });
      } catch (err) {
        out.push({ email, ok: false, error: errMessage(err, 'Failed to invite') });
      }
    }
    setResults(out);
    setEmails(out.filter(r => !r.ok).map(r => r.email));
    setDraft('');
    setSending(false);
    if (out.some(r => r.ok)) onInvited();
  };

  const saved = results?.filter(r => r.ok) || [];
  const mailed = saved.filter(r => r.emailSent);
  const unmailed = saved.filter(r => !r.emailSent);
  const failed = results?.filter(r => !r.ok) || [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="MEMBERS"
      title="Invite members"
      subtitle="They get an email with a link and sign in with Google."
      footer={(
        <>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Invitations expire after 7 days.</span>
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>
              {results ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending || toSend.length === 0}
              className={primaryBtn}
              style={primaryBtnStyle}
            >
              {sending
                ? 'Sending…'
                : toSend.length ? `Send ${toSend.length} invitation${toSend.length === 1 ? '' : 's'}` : 'Send invitations'}
            </button>
          </div>
        </>
      )}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label htmlFor="inv-email" className={fieldLabel} style={fieldLabelStyle}>Email addresses</label>
          <div
            className="min-h-[52px] box-border flex flex-wrap items-center gap-1.5 px-2.5 py-2 rounded-[10px] border"
            style={{ background: 'var(--surf-bg)', borderColor: error ? 'var(--sev-crit-fg)' : 'var(--surf-line-strong)' }}
          >
            {emails.map(addr => (
              <span
                key={addr}
                className="h-7 inline-flex items-center gap-1.5 pl-2.5 pr-1 rounded-[7px] border font-mono text-xs"
                style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line)' }}
              >
                {addr}
                <button
                  type="button"
                  aria-label={`Remove ${addr}`}
                  onClick={() => setEmails(prev => prev.filter(p => p !== addr))}
                  className="w-[22px] h-[22px] flex items-center justify-center"
                  style={{ color: 'var(--ink-faint)' }}
                >
                  <X className="w-3 h-3" strokeWidth={2.2} />
                </button>
              </span>
            ))}
            <input
              id="inv-email"
              type="text"
              inputMode="email"
              autoComplete="off"
              value={draft}
              onChange={e => { setDraft(e.target.value); if (error) setError(null); }}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onBlur={() => draft.trim() && addEmails(draft)}
              placeholder={emails.length ? 'Add another email' : 'colleague@company.com'}
              aria-invalid={!!error}
              aria-describedby="inv-email-help"
              className="flex-grow min-w-[140px] h-8 bg-transparent border-0 outline-none text-sm"
              style={{ color: 'var(--ink-text)' }}
            />
          </div>
          <span id="inv-email-help" className="text-xs" style={{ color: error ? 'var(--sev-crit-fg)' : 'var(--ink-faint)' }}>
            {error || 'Paste a list, or separate addresses with commas. They sign in with Google using this email.'}
          </span>
        </div>

        <fieldset className="m-0 p-0 border-0 flex flex-col gap-2">
          <legend className={`p-0 mb-2 ${fieldLabel}`} style={fieldLabelStyle}>Role</legend>
          {roles.map(r => {
            const on = r.name === role;
            return (
              <label
                key={r.id}
                className="flex items-start gap-3 px-3.5 py-3 rounded-[10px] border cursor-pointer"
                style={{ borderColor: on ? 'var(--accent)' : 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'var(--surf-bg)' }}
              >
                <input
                  type="radio"
                  name="invite-role"
                  checked={on}
                  onChange={() => setRole(r.name)}
                  className="mt-[3px] w-4 h-4"
                  style={{ accentColor: 'var(--accent)' }}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold">{r.name}</span>
                  {r.description && (
                    <span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-muted)' }}>{r.description}</span>
                  )}
                </span>
              </label>
            );
          })}
          {roles.length === 0 && (
            <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>No roles in this organisation yet.</span>
          )}
        </fieldset>

        {results && (
          <div role="status" className="flex flex-col gap-3 px-4 py-3.5 rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>
            {saved.length > 0 && (
              <span className="flex items-center gap-2 text-[13.5px] font-medium">
                <span className="w-2 h-2 rounded-full" style={{ background: 'var(--sev-good-fg)' }} />
                {saved.length} invitation{saved.length === 1 ? '' : 's'} saved · {mailed.length} email{mailed.length === 1 ? '' : 's'} sent
              </span>
            )}
            {unmailed.map(r => (
              <div key={r.email} className="flex flex-col gap-2">
                <span className="flex items-start gap-2 text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
                  <span className="w-2 h-2 rounded-sm mt-[5px] shrink-0" style={{ background: 'var(--sev-med-fg)' }} />
                  Email delivery failed for {r.email}. Share this link with them directly.
                </span>
                {r.link && (
                  <div className="flex items-center gap-2">
                    <span className="flex-grow min-w-0 h-10 box-border flex items-center px-3 rounded-lg border font-mono text-xs truncate select-all" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
                      {r.link}
                    </span>
                    <CopyButton text={r.link} />
                  </div>
                )}
              </div>
            ))}
            {failed.map(r => (
              <span key={r.email} className="flex items-start gap-2 text-[12.5px] leading-normal" style={{ color: 'var(--sev-crit-fg)' }}>
                <span className="w-2 h-2 rounded-sm mt-[5px] shrink-0" style={{ background: 'var(--sev-crit-fg)' }} />
                {r.email}: {r.error}
              </span>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default InviteDrawer;
